import { Button } from '@affine/component/ui/button';
import { notify } from '@affine/component/ui/notification';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { AuthService, ServerService } from '@affine/core/modules/cloud';
import { UrlService } from '@affine/core/modules/url';
import { UserFriendlyError } from '@affine/error';
import { OAuthProviderType } from '@affine/graphql';
import track from '@affine/track';
import {
  AppleIcon,
  GithubIcon,
  GoogleIcon,
  LockIcon,
} from '@blocksuite/icons/rc';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import {
  type ReactElement,
  type SVGAttributes,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { clerkFapiUrl } from '../../sign-in/clerk-dev-browser';

const OAuthProviderMap: Record<
  OAuthProviderType,
  {
    icon: ReactElement<SVGAttributes<SVGElement>>;
  }
> = {
  [OAuthProviderType.Google]: {
    icon: <GoogleIcon />,
  },

  [OAuthProviderType.GitHub]: {
    icon: <GithubIcon />,
  },

  [OAuthProviderType.OIDC]: {
    icon: <LockIcon />,
  },

  [OAuthProviderType.Apple]: {
    icon: <AppleIcon />,
  },
};

const globalSilentLoginState = {
  running: false,
};

function useOidcSilentLogin(
  redirectUrl: string | undefined,
  serverService: ServerService,
  auth: AuthService
) {
  const oauth = useLiveData(serverService.server.features$.map(r => r?.oauth));
  const oauthProviders = useLiveData(
    serverService.server.config$.map(r => r?.oauthProviders)
  );
  const sessionStatus = useLiveData(auth.session.status$);
  const silentAttemptedRef = useRef(false);

  useEffect(() => {
    if (
      !oauth ||
      !oauthProviders?.includes(OAuthProviderType.OIDC) ||
      typeof window === 'undefined' ||
      BUILD_CONFIG.isNative ||
      sessionStatus === 'authenticated' ||
      silentAttemptedRef.current ||
      globalSilentLoginState.running
    ) {
      return;
    }

    silentAttemptedRef.current = true;
    globalSilentLoginState.running = true;

    let cancelled = false;
    let iframe: HTMLIFrameElement | null = null;
    const timeouts: number[] = [];
    let messageOrigin: string | null = null;
    let stateFromPreflight: string | undefined;
    let succeeded = false;

    const cleanup = (didSucceed = false) => {
      if (cancelled) {
        return;
      }
      succeeded = succeeded || didSucceed;
      cancelled = true;
      timeouts.forEach(id => window.clearTimeout(id));
      if (iframe?.parentNode) {
        iframe.parentNode.removeChild(iframe);
        iframe = null;
      }
      window.removeEventListener('message', handleMessage);
      if (!succeeded) {
        silentAttemptedRef.current = false;
      }
      globalSilentLoginState.running = false;
    };

    const scheduleRevalidate = () => {
      if (cancelled) {
        return;
      }
      timeouts.push(
        window.setTimeout(() => {
          if (!cancelled) {
            void auth.session.revalidate();
          }
        }, 500)
      );
      timeouts.push(
        window.setTimeout(() => {
          if (!cancelled) {
            void auth.session.revalidate();
          }
        }, 1500)
      );
      timeouts.push(
        window.setTimeout(() => {
          if (!cancelled) {
            void auth.session.revalidate();
          }
        }, 4000)
      );
    };

    const handleMessage = (event: MessageEvent) => {
      if (
        cancelled ||
        !messageOrigin ||
        event.origin !== messageOrigin ||
        !event.data ||
        event.data.type !== 'authorization_response'
      ) {
        return;
      }

      const response = event.data.response ?? {};

      if (response.error) {
        cleanup(false);
        return;
      }

      const code: string | undefined = response.code;
      const stateFromMessage: string | undefined = response.state;

      if (!code) {
        cleanup(false);
        return;
      }

      cleanup(true);

      const finalState = stateFromMessage ?? stateFromPreflight ?? '';

      void auth
        .signInOauth(code, finalState, OAuthProviderType.OIDC)
        .then(() => {
          return auth.session.revalidate();
        })
        .catch(() => {
          silentAttemptedRef.current = false;
        });
    };

    window.addEventListener('message', handleMessage);

    const runPreflight = async () => {
      // Initialize dev browser to get __clerk_db_jwt for dev instances
      const devBrowserUrl = await clerkFapiUrl('/v1/client');
      const dbJwtMatch = new URL(devBrowserUrl).searchParams.get(
        '__clerk_db_jwt'
      );

      const params: Record<string, string> = {
        prompt: 'none',
        response_mode: 'web_message',
        origin: window.location.origin,
      };
      if (dbJwtMatch) {
        params.__clerk_db_jwt = dbJwtMatch;
      }
      params.redirect_uri =
        redirectUrl ?? `${window.location.origin}/oauth/callback`;
      const { url, state } = await auth.oauthPreflight(
        OAuthProviderType.OIDC,
        'web',
        redirectUrl,
        params
      );
      if (cancelled) {
        return;
      }
      stateFromPreflight = state;
      messageOrigin = new URL(url).origin;
      iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.opacity = '0';
      iframe.style.pointerEvents = 'none';
      iframe.setAttribute('tabindex', '-1');
      iframe.src = url;
      iframe.addEventListener('load', scheduleRevalidate);
      document.body.appendChild(iframe);
      scheduleRevalidate();
      timeouts.push(
        window.setTimeout(() => {
          if (!cancelled) {
            cleanup();
          }
        }, 10000)
      );
    };

    runPreflight().catch(() => {
      cleanup(false);
    });

    return () => cleanup(false);
  }, [auth, oauth, oauthProviders, redirectUrl, serverService, sessionStatus]);
}

export function OAuthSilentLogin({ redirectUrl }: { redirectUrl?: string }) {
  const serverService = useServiceOptional(ServerService);
  const auth = useServiceOptional(AuthService);

  if (!serverService || !auth) {
    return null;
  }

  return (
    <OidcSilentLoginRunner
      redirectUrl={redirectUrl}
      serverService={serverService}
      authService={auth}
    />
  );
}

// Note: Auto-redirect to OIDC is now handled in the index page (pages/index/index.tsx)
// This component is kept for backwards compatibility but the main auto-redirect
// logic has been moved to ensure it runs at page load, not when the modal opens.

function OidcSilentLoginRunner({
  redirectUrl,
  serverService,
  authService,
}: {
  redirectUrl?: string;
  serverService: ServerService;
  authService: AuthService;
}) {
  useOidcSilentLogin(redirectUrl, serverService, authService);
  return null;
}

export function OAuth({ redirectUrl }: { redirectUrl?: string }) {
  const serverService = useService(ServerService);
  const urlService = useService(UrlService);
  const oauth = useLiveData(serverService.server.features$.map(r => r?.oauth));
  const oauthProviders = useLiveData(
    serverService.server.config$.map(r => r?.oauthProviders)
  );
  const auth = useService(AuthService);

  useOidcSilentLogin(redirectUrl, serverService, auth);

  // Note: Auto-redirect to OIDC is now handled in pages/index/index.tsx
  // The redirect happens at page load, before the modal is opened

  const onContinue = useAsyncCallback(
    async (provider: OAuthProviderType) => {
      track.$.$.auth.signIn({ method: 'oauth', provider });

      const open: () => Promise<void> | void = BUILD_CONFIG.isNative
        ? async () => {
            try {
              const scheme = urlService.getClientScheme();
              const options = await auth.oauthPreflight(
                provider,
                scheme ?? 'web',
                redirectUrl
              );
              urlService.openPopupWindow(options.url);
            } catch (e) {
              notify.error(UserFriendlyError.fromAny(e));
            }
          }
        : () => {
            const params = new URLSearchParams();

            params.set('provider', provider);

            if (redirectUrl) {
              params.set('redirect_uri', redirectUrl);
            }

            const oauthUrl =
              serverService.server.baseUrl +
              `/oauth/login?${params.toString()}`;

            urlService.openPopupWindow(oauthUrl);
          };

      const ret = open();

      if (ret instanceof Promise) {
        await ret;
      }
    },
    [urlService, redirectUrl, serverService, auth]
  );

  if (!oauth) {
    return null;
  }

  return oauthProviders?.map(provider => {
    return (
      <OAuthProvider
        key={provider}
        provider={provider}
        onContinue={onContinue}
      />
    );
  });
}

interface OauthProviderProps {
  provider: OAuthProviderType;
  onContinue: (provider: OAuthProviderType) => void;
}

function OAuthProvider({ onContinue, provider }: OauthProviderProps) {
  const { icon } =
    provider in OAuthProviderMap
      ? OAuthProviderMap[provider]
      : { icon: undefined };

  const onClick = useCallback(() => {
    onContinue(provider);
  }, [onContinue, provider]);

  return (
    <Button
      variant={provider === OAuthProviderType.Apple ? 'custom' : 'primary'}
      block
      size="extraLarge"
      style={{ width: '100%' }}
      prefix={icon}
      onClick={onClick}
    >
      Continue with {provider}
    </Button>
  );
}
